// Provider catalogue (shadow-AI) admin API client + types.
// The bundled catalogue is the read-only base; these endpoints manage the SQLite
// overlay (custom additions/edits/disables + fetched candidates awaiting approval).

export type ProviderStatus = 'active' | 'pending' | 'rejected' | 'disabled';
export type RiskLevel = 'sanctioned' | 'tolerated' | 'unsanctioned' | 'high_risk';

export interface CatalogProvider {
  id: string;
  label: string;
  kind?: string;
  type?: 'llm' | 'mcp';
  riskLevel?: RiskLevel;
  match: string[];
  apps?: string[];
  // Present only on the ?include=all admin view:
  source?: 'bundled' | 'manual' | 'import' | string;
  status?: ProviderStatus;
}

export interface ProviderInput {
  id: string;
  label: string;
  kind?: string;
  type?: 'llm' | 'mcp';
  riskLevel?: RiskLevel;
  match: string[];
  apps?: string[];
}

export interface FeedStatus {
  feed: string;
  ok: boolean;
  count: number;
  error?: string;
}

async function asJson(res: Response): Promise<any> {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || `Request failed (${res.status})`);
  return data;
}

/** Full admin view: bundled base + overlay rows (incl. pending/disabled). */
export async function listAllProviders(apiUrl: string): Promise<CatalogProvider[]> {
  const data = await asJson(await fetch(`${apiUrl}/admin/discovery/providers?include=all`));
  return Array.isArray(data.providers) ? data.providers : [];
}

export async function saveProvider(apiUrl: string, input: ProviderInput): Promise<CatalogProvider> {
  const data = await asJson(
    await fetch(`${apiUrl}/admin/discovery/providers`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    })
  );
  return data.provider;
}

export async function deleteProvider(apiUrl: string, id: string): Promise<void> {
  await asJson(await fetch(`${apiUrl}/admin/discovery/providers/${encodeURIComponent(id)}`, { method: 'DELETE' }));
}

export async function decideProvider(
  apiUrl: string,
  id: string,
  decision: 'approved' | 'rejected' | 'disabled' | 'active'
): Promise<void> {
  await asJson(
    await fetch(`${apiUrl}/admin/discovery/providers/${encodeURIComponent(id)}/decision`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ decision }),
    })
  );
}

export async function importProviders(
  apiUrl: string,
  payload: any
): Promise<{ imported: number; errors: Array<{ id?: string; error: string }> }> {
  return asJson(
    await fetch(`${apiUrl}/admin/discovery/providers/import`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
  );
}

export async function fetchCandidates(
  apiUrl: string
): Promise<{ staged: number; skipped: number; feeds: FeedStatus[]; candidates: CatalogProvider[] }> {
  return asJson(await fetch(`${apiUrl}/admin/discovery/providers/fetch`, { method: 'POST' }));
}

/**
 * Clear all pending (fetched, unapproved) candidates at once.
 *  - 'reject': tombstone them so future fetches won't re-stage the same vendors.
 *  - 'delete': remove them outright (may re-appear on the next fetch).
 */
export async function clearPending(
  apiUrl: string,
  mode: 'reject' | 'delete' = 'reject'
): Promise<{ cleared: number; mode: string }> {
  return asJson(
    await fetch(`${apiUrl}/admin/discovery/providers/clear-pending`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ mode }),
    })
  );
}

// ---- per-exact-host Discovery-page corrections --------------------------------
// Distinct from the provider-catalog CRUD above: these correct classification
// for ONE observed host (e.g. "metrics.cursor.sh is not AI") without touching
// the provider's other real hosts. Persists — unlike deleting a discovered
// endpoint row, which just reappears on the next traffic.

/** Mark an exact discovered host as not AI — it stops classifying as shadow-AI everywhere. */
export async function markEndpointNotAi(apiUrl: string, host: string): Promise<void> {
  await asJson(
    await fetch(`${apiUrl}/admin/discovery/endpoints/${encodeURIComponent(host)}/not-ai`, { method: 'POST' })
  );
}

/** Confirm an exact discovered host as AI, tagging it to a provider + risk level. */
export async function confirmEndpointAi(
  apiUrl: string,
  host: string,
  providerId: string,
  riskLevel?: RiskLevel
): Promise<void> {
  await asJson(
    await fetch(`${apiUrl}/admin/discovery/endpoints/${encodeURIComponent(host)}/confirm-ai`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ providerId, riskLevel }),
    })
  );
}

/** Remove a per-host override — the host reverts to normal catalogue matching. */
export async function clearEndpointOverride(apiUrl: string, host: string): Promise<void> {
  await asJson(
    await fetch(`${apiUrl}/admin/discovery/endpoints/${encodeURIComponent(host)}/override`, { method: 'DELETE' })
  );
}
